/**
 * THE TA TEAM — one roster, shared, read by every tab that plans against it.
 *
 * Before this the cockpit held three answers to "how big is the team": per-cluster FTE numbers on
 * the Google Sheet (Planning), a per-person table in `localStorage` (Role Prioritisation), and a
 * recruiter→cluster map in a checked-in config file (Cluster Lead Check-In). Three sources, three
 * answers, and every capacity figure in the cockpit sitting on top of them.
 *
 * Set here, on Planning. Everything else reads it.
 *
 * ⚠️ HOURS, never FTE. A working model is contracted hours a week; FTE is derived against
 * `fullWeekHours`, which is itself an editable assumption. Storing 0.5 loses whether that is a
 * 20-hour week against a 40-hour norm, or something else.
 */

export const CLUSTER_KEYS = ['Growth', 'Operations', 'Core Functions'] as const;
export type ClusterKey = (typeof CLUSTER_KEYS)[number];

/** Someone on the team who carries no cluster's load. Not a cluster; the absence of one. */
export const UNASSIGNED = 'Unassigned';

export type MemberKind = 'recruiter' | 'intern';

/** Every cluster someone can carry, plus the absence of one. */
export type MemberCluster = ClusterKey | typeof UNASSIGNED;

export interface TeamMember {
  /**
   * Stable across renames and re-sorts. Support allocations on Role Prioritisation name a lender by
   * id, so identifying people by name or array position would re-point an allocation at a
   * colleague the moment someone was renamed or the list re-ordered.
   */
  id: string;
  name: string;
  role: string;
  /**
   * ⚠️ Narrowed on purpose. The capacity model only ever sums `CLUSTER_KEYS`, so a cluster outside
   * this set does not error — it silently drops that person's hours out of every figure, which is
   * the worst possible failure for a roster whose whole job is saying what the team can do. The
   * server refuses one too (`cleanMember`).
   */
  cluster: MemberCluster;
  /** Contracted hours a week. */
  hours: number;
  kind: MemberKind;
  /**
   * Whether these hours count as hiring capacity. True unless explicitly false — a new recruiter is
   * capacity without anyone remembering a flag, and the Head of TA is the row that turns it off, so
   * her hours sit outside every figure until she chooses to offer them.
   */
  countsTowardHiring?: boolean;
}

/** What the server stores, with who last changed it. */
export interface StoredTeam {
  members: TeamMember[];
  savedBy: string;
  savedAt: string;
  note?: string;
  history?: { members: TeamMember[]; savedBy: string; savedAt: string; note?: string }[];
}

/**
 * The seed — the T3 2026 roster, used only until the server has one.
 *
 * ⚠️ It is a STARTING POINT, not a fallback that silently substitutes for the real thing: the tab
 * says when it is showing this rather than a saved roster, because planning against a team nobody
 * confirmed is exactly the failure the shared store was built to end.
 */
export const SEED_TEAM: TeamMember[] = [
  { id: 'lola-hense', name: 'Lola Hense', role: 'Recruiter', cluster: 'Growth', hours: 40, kind: 'recruiter' },
  { id: 'annika-vestewig', name: 'Annika Vestewig', role: 'Recruiter', cluster: 'Growth', hours: 40, kind: 'recruiter' },
  { id: 'louisa-budde', name: 'Louisa Budde', role: 'Recruiter', cluster: 'Growth', hours: 40, kind: 'recruiter' },
  { id: 'luisa-heinzinger', name: 'Luisa Heinzinger', role: 'Recruiter', cluster: 'Operations', hours: 40, kind: 'recruiter' },
  { id: 'antonia-conrad', name: 'Antonia Conrad', role: 'Recruiter', cluster: 'Operations', hours: 40, kind: 'recruiter' },
  { id: 'anna-thiel', name: 'Anna Thiel', role: 'Recruiter', cluster: 'Core Functions', hours: 40, kind: 'recruiter' },
  { id: 'annika-lutzenberger', name: 'Annika Lutzenberger', role: 'Recruiter', cluster: 'Core Functions', hours: 40, kind: 'recruiter' },
  {
    id: 'theresa-machnik',
    name: 'Theresa Machnik',
    role: 'Head of TA',
    cluster: 'Core Functions',
    hours: 40,
    kind: 'recruiter',
    countsTowardHiring: false,
  },
];

/** Contracted hours a working model can take. The list, so the same models exist everywhere. */
export const WORKING_MODELS = [
  { hours: 40, label: 'Full-time · 40 h' },
  { hours: 32, label: '0.8 FTE · 32 h' },
  { hours: 30, label: '0.75 FTE · 30 h' },
  { hours: 20, label: '0.5 FTE · 20 h' },
  { hours: 16, label: 'Working student · 16 h' },
  { hours: 0, label: 'Away this trimester · 0 h' },
];

/** Whether someone's hours count toward hiring capacity. Absent means yes. */
export const isPlanned = (m: TeamMember): boolean => m.countsTowardHiring !== false;

export const isIntern = (m: TeamMember): boolean => m.kind === 'intern';

let seq = 0;

/** A new row. The id only has to be unique within the roster, and is generated rather than typed. */
export function newMember(kind: MemberKind, cluster: MemberCluster = UNASSIGNED): TeamMember {
  seq += 1;
  return {
    id: `${kind}-${Date.now().toString(36)}-${seq}`,
    name: kind === 'intern' ? 'New intern' : 'New recruiter',
    role: kind === 'intern' ? 'Working student' : 'Recruiter',
    cluster,
    hours: kind === 'intern' ? 16 : 40,
    kind,
  };
}
